import type { WidgetConfig } from '@/types/widget';
import type {
  QuerySpec,
  FilterExpression,
  Aggregation,
  ColumnTransform,
  DateBucket,
} from '@/types/query';
import type { ColumnMeta } from '@/types/dataset';
import { isDateType } from '@/features/filters/column-types';

/** Fixed alias used for the single metric on every widget. */
export const WIDGET_METRIC_ALIAS = 'metric';

/** Default row limit for chart widgets when the user hasn't set one. */
export const DEFAULT_CHART_LIMIT = 10;

/** Default number of breakdown series when `widget.breakdownBy` is set. */
export const DEFAULT_BREAKDOWN_LIMIT = 8;

/**
 * Safety cap on rows returned for breakdown queries. Each X × breakdown
 * combination is one row; without a cap a fully-cardinalised pair would
 * return millions of rows and stall the pivot. 10 000 covers e.g. 365
 * days × 27 drugs or 1 200 categories × 8 series before truncation.
 */
const BREAKDOWN_SAFETY_ROW_CAP = 10_000;

/** Default date bucket when the groupBy column is DATE/TIMESTAMP. */
export const DEFAULT_DATE_BUCKET: DateBucket = 'month';

/**
 * Translate a user-editable `WidgetConfig` + the current global filter
 * list into a `QuerySpec` that the engine can execute.
 *
 * KPI widgets have no groupBy and no limit.
 * Chart widgets group by their `groupBy` column and are sorted by the
 * primary metric descending so the top-N is deterministic — except when
 * the groupBy is a DATE/TIMESTAMP, in which case the sort is by the
 * groupBy column ascending (chronological order) and the worker is
 * told to bucket the column via DATE_TRUNC.
 *
 * Bar and line widgets may include extra metrics for multi-series.
 *
 * `groupByColumnSqlType` is the DuckDB sqlType of `widget.groupBy`,
 * looked up by the caller (typically the `Widget` dispatcher) from the
 * dataset registry. Pass `undefined` for KPI widgets or when the column
 * type isn't known yet — bucketing/chronological sort are simply
 * skipped in that case.
 */
export function buildSpec(
  widget: WidgetConfig,
  filters: FilterExpression[],
  groupByColumnSqlType?: string,
  valueGroupings?: Record<string, ColumnTransform>,
): QuerySpec {
  const metric = {
    column: widget.metric.column,
    aggregation: widget.metric.aggregation,
    alias: WIDGET_METRIC_ALIAS,
  };

  // Only include transforms that target a column referenced by this
  // widget — irrelevant groupings would just inflate the cache key.
  const relevantTransforms = pickRelevantTransforms(
    widget,
    filters,
    valueGroupings,
  );

  if (widget.kind === 'kpi') {
    const kpi: QuerySpec = {
      source: widget.datasetId,
      filters,
      groupBy: [],
      metrics: [metric],
    };
    if (relevantTransforms) kpi.transforms = relevantTransforms;
    return kpi;
  }

  const metrics = [metric];

  // Multi-series: append extra metrics with unique aliases.
  if (widget.extraMetrics) {
    for (let i = 0; i < widget.extraMetrics.length; i++) {
      const em = widget.extraMetrics[i]!;
      metrics.push({
        column: em.column,
        aggregation: em.aggregation,
        alias: `metric_${i + 1}`,
      });
    }
  }

  const hasBreakdown = !!widget.breakdownBy && !widget.extraMetrics?.length;
  const groupBy = widget.groupBy ? [widget.groupBy] : [];
  if (hasBreakdown) groupBy.push(widget.breakdownBy!);

  const isTimeAxis =
    !!widget.groupBy &&
    !!groupByColumnSqlType &&
    isDateType(groupByColumnSqlType);

  // `widget.limit === 0` is the explicit "unlimited" sentinel — surfaces
  // in the Query Builder as a "Limitsiz" checkbox. Anything else (or
  // undefined) falls back to DEFAULT_CHART_LIMIT.
  // Breakdown queries can't use the per-group LIMIT directly: each row is
  // one (X × breakdown) combination, and a 10-row cap would slice the
  // pivot mid-category. Apply a generous safety cap instead and let the
  // client-side pivot truncate to the user's primary + breakdown limits.
  const spec: QuerySpec = {
    source: widget.datasetId,
    filters,
    groupBy,
    metrics,
    sort: isTimeAxis
      ? [{ column: widget.groupBy!, direction: 'asc' }]
      : hasBreakdown
        ? [{ column: widget.groupBy!, direction: 'asc' }]
        : [{ column: WIDGET_METRIC_ALIAS, direction: 'desc' }],
  };
  if (hasBreakdown) {
    spec.limit = BREAKDOWN_SAFETY_ROW_CAP;
  } else if (widget.limit !== 0) {
    spec.limit = widget.limit ?? DEFAULT_CHART_LIMIT;
  }

  if (isTimeAxis) {
    spec.groupByBuckets = {
      [widget.groupBy!]: widget.dateBucket ?? DEFAULT_DATE_BUCKET,
    };
  }

  if (relevantTransforms) spec.transforms = relevantTransforms;

  return spec;
}

/**
 * Narrow the dataset-wide valueGroupings to the subset relevant for this
 * widget — the groupBy column and any filtered column. Returns undefined
 * when nothing applies so the QuerySpec stays slim and cache keys stable.
 */
function pickRelevantTransforms(
  widget: WidgetConfig,
  filters: FilterExpression[],
  valueGroupings?: Record<string, ColumnTransform>,
): Record<string, ColumnTransform> | undefined {
  if (!valueGroupings) return undefined;
  const keys = new Set<string>();
  if (widget.groupBy) keys.add(widget.groupBy);
  for (const f of filters) keys.add(f.column);
  const out: Record<string, ColumnTransform> = {};
  let any = false;
  for (const k of keys) {
    const t = valueGroupings[k];
    if (t) {
      out[k] = t;
      any = true;
    }
  }
  return any ? out : undefined;
}

// ---------------------------------------------------------------------------
// Aggregation availability by column type
// ---------------------------------------------------------------------------

const NUMERIC_SQL_TYPES = [
  'DOUBLE',
  'BIGINT',
  'INTEGER',
  'FLOAT',
  'DECIMAL',
  'REAL',
  'SMALLINT',
  'TINYINT',
  'HUGEINT',
];

const NUMERIC_AGGS: readonly Aggregation[] = [
  'sum',
  'avg',
  'min',
  'max',
  'median',
  'count',
  'count_distinct',
] as const;

const NON_NUMERIC_AGGS: readonly Aggregation[] = [
  'count',
  'count_distinct',
] as const;

/** True when a DuckDB sqlType string corresponds to a numeric column. */
export function isNumericSqlType(sqlType: string): boolean {
  const u = sqlType.toUpperCase();
  return NUMERIC_SQL_TYPES.some((t) => u.includes(t));
}

/**
 * Return the set of aggregations valid for a column of the given type.
 * Numeric columns get all aggregations; non-numeric are restricted to
 * `count` / `count_distinct` (sum/avg on a VARCHAR is a SQL error).
 */
export function aggregationsForColumn(
  column: ColumnMeta | undefined,
): readonly Aggregation[] {
  if (!column) return NON_NUMERIC_AGGS;
  return isNumericSqlType(column.sqlType) ? NUMERIC_AGGS : NON_NUMERIC_AGGS;
}

/**
 * Non-translated fallback labels for aggregations. UI callers should
 * prefer `t(aggregationLabelKey(agg))`; this map is kept for non-React
 * contexts (tests, worker, log output) where i18n isn't wired.
 */
export const AGGREGATION_LABELS: Record<Aggregation, string> = {
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
  median: 'Median',
  count: 'Count',
  count_distinct: 'Unique count',
};

/**
 * Map an `Aggregation` to its `queryBuilder.aggregations.*` i18n key —
 * parallel to `opLabelKey` in `features/filters/column-types.ts`. Callers
 * pass the result to `t(...)` so the label tracks the active locale.
 */
export function aggregationLabelKey(agg: Aggregation): string {
  return `queryBuilder.aggregations.${agg}`;
}
